/**
 * API service for communicating with the backend API.
 *
 * The backend is stateless: every request carries the browser-owned form
 * data and chat history with it. There are only two endpoints -- streaming
 * chat and PDF generation.
 */

import config from '../utils/config';

/**
 * Send a chat message and stream the AI's reply token by token.
 * @param {Object} params - {sessionId, message, formData, step, history}
 * @param {Function} onDelta - called with the accumulated text after each chunk
 * @returns {Promise<string>} - the complete response text
 */
const streamChatMessage = async ({ sessionId, message, formData = null, step = null, history = null }, onDelta) => {
  const response = await fetch(`${config.API_URL}/chat/stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId, message, formData, step, history })
  });

  if (!response.ok || !response.body) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Chat request failed with status: ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let fullText = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const events = buffer.split('\n\n');
    buffer = events.pop(); // keep any incomplete event in the buffer

    for (const event of events) {
      const line = event.trim();
      if (!line.startsWith('data:')) continue;

      const payload = JSON.parse(line.slice(5).trim());
      if (payload.error) {
        throw new Error(payload.error);
      }
      if (payload.delta) {
        fullText += payload.delta;
        if (onDelta) onDelta(fullText);
      }
    }
  }

  if (!fullText) {
    throw new Error('The AI service returned an empty response. Please try again.');
  }

  return fullText;
};

/**
 * Generate a PDF report for a session. The backend streams the PDF straight
 * back (nothing is stored server-side), so this resolves to a Blob.
 * @param {string} sessionId - Session ID to generate report for
 * @param {Object} formData - Browser-owned form data
 * @param {Array} history - Browser-owned chat history
 * @returns {Promise<{blob: Blob, filename: string}>}
 */
const generatePdfReport = async (sessionId, formData = null, history = null) => {
  if (!sessionId) {
    throw new Error('Session ID is required to generate a PDF report');
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), config.TIMEOUTS.llmApiRequestTimeout);

  try {
    const response = await fetch(`${config.API_URL}/pdf/generate-pdf`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ sessionId, formData, history }),
      signal: controller.signal
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `PDF generation failed with status: ${response.status}`);
    }

    const blob = await response.blob();
    if (blob.size === 0) {
      throw new Error('Generated PDF is empty');
    }

    // Filename comes from the Content-Disposition header
    const disposition = response.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="([^"]+)"/);
    const filename = match ? match[1] : `MedicalReport_${new Date().toISOString().split('T')[0]}.pdf`;

    return { blob, filename };
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('PDF generation timed out. Please try again later.');
    }
    console.error('PDF generation API error:', error);
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
};

const api = {
  streamChatMessage,
  generatePdfReport,
};

export default api;
